/**
 * whatsapp.js
 * Serviço de envio de mensagens WhatsApp via API semfila.co.
 *
 * Fluxo:
 *  1. Obtém IdToken via AWS Cognito (USER_PASSWORD_AUTH)
 *  2. Cacheia o token em memória enquanto não expirar (ExpiresIn = 3600 s)
 *  3. Envia a mensagem usando o template configurado
 *
 * Todas as credenciais vivem aqui — não expor em commits públicos.
 */

// ── Cognito config ──────────────────────────────────────────────────────────
import { config } from "../config";

const COGNITO_ENDPOINT = config.COGNITO_ENDPOINT;
const COGNITO_CLIENT_ID = config.COGNITO_CLIENT_ID;
const COGNITO_USERNAME = config.COGNITO_USERNAME;
const COGNITO_PASSWORD = config.COGNITO_PASSWORD;

// ── API semfila config ──────────────────────────────────────────────────────
const SEMFILA_ENDPOINT = config.SEMFILA_ENDPOINT;

// ── Evento ANM 2026 (fixo) ──────────────────────────────────────────────────
const EVENT_DATA = config.EVENT_DATA;
const EVENT_LOCAL = config.EVENT_LOCAL;
const EVENT_MAPA = config.EVENT_MAPA;

// ── Token cache (módulo-level, sobrevive entre re-renders) ──────────────────
const _cache = {
    idToken: null,
    expiresAt: 0, // timestamp ms
};

/**
 * Retorna um IdToken válido, renovando via Cognito se necessário.
 * Reusa o token enquanto restar pelo menos 60 s de validade.
 */
async function getIdToken() {
    const now = Date.now();
    if (_cache.idToken && now < _cache.expiresAt - 60_000) {
        return _cache.idToken;
    }

    const resp = await fetch(COGNITO_ENDPOINT, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-amz-json-1.1",
            "X-Amz-Target":
                "AWSCognitoIdentityProviderService.InitiateAuth",
        },
        body: JSON.stringify({
            AuthFlow: "USER_PASSWORD_AUTH",
            ClientId: COGNITO_CLIENT_ID,
            AuthParameters: {
                USERNAME: COGNITO_USERNAME,
                PASSWORD: COGNITO_PASSWORD,
            },
        }),
    });

    if (!resp.ok) {
        const errBody = await resp.text();
        throw new Error(`Cognito auth failed: ${resp.status} – ${errBody}`);
    }

    const json = await resp.json();
    const auth = json?.AuthenticationResult;
    if (!auth?.IdToken) {
        throw new Error("Cognito: IdToken ausente na resposta");
    }

    // Guarda em cache usando ExpiresIn (segundos)
    const expiresIn = auth.ExpiresIn ?? 3600;
    _cache.idToken = auth.IdToken;
    _cache.expiresAt = now + expiresIn * 1000;

    return _cache.idToken;
}

/**
 * Envia mensagem de confirmação de inscrição no WhatsApp.
 *
 * @param {object} params
 * @param {string} params.phone        - Telefone do participante (somente dígitos, com DDI)
 * @param {string|number} params.personId - ID do participante para gerar QR Code
 */
async function sendConfirmacaoInscricao({ phone, personId }) {
    if (!phone) throw new Error("WhatsApp: telefone é obrigatório");
    if (!personId) throw new Error("WhatsApp: personId é obrigatório");

    // Garante apenas dígitos (o campo phone já vem limpo do formulário)
    const cleanPhone = String(phone).replace(/\D/g, "");

    const linkQrcode = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(personId)}`;

    const idToken = await getIdToken();

    const resp = await fetch(SEMFILA_ENDPOINT, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
            templateName: "confirmacao_inscricao_anm_2026",
            languageCode: "pt_BR",
            recipients: [
                {
                    phone: cleanPhone,
                    parameters: {
                        data_evento: EVENT_DATA,
                        local_evento: EVENT_LOCAL,
                        link_mapa: EVENT_MAPA,
                        link_qrcode: linkQrcode,
                    },
                },
            ],
        }),
    });

    if (!resp.ok) {
        const errBody = await resp.text();
        throw new Error(`WhatsApp API error: ${resp.status} – ${errBody}`);
    }

    return await resp.json();
}

// ── Exports ─────────────────────────────────────────────────────────────────
const WhatsAppService = {
    sendConfirmacaoInscricao,
    /** Força reset do cache (útil em testes) */
    _resetCache: () => { _cache.idToken = null; _cache.expiresAt = 0; },
};

export default WhatsAppService;
